import {
  ControlButton,
  Controls,
  Edge,
  MiniMap,
  OnConnect,
  ReactFlow,
  useEdgesState,
  useNodesState
} from '@xyflow/react'

import '@xyflow/react/dist/style.css'
import { WaterStation } from '../../schemas'
import { useEffect, useState } from 'react'
import _ from 'lodash'
import { CanvasNodes, CanvasNodeTypes } from './CanvasNodes'
import { Connection } from '~/types'
import { useThemeAppearance } from '~/features/layout/hooks'
import { LuLock, LuLockOpen } from 'react-icons/lu'
import { Tooltip } from '@radix-ui/themes'
import { DataSource } from '@willie/schemas'

const X_OFFSET = 300
const Y_OFFSET = 100

type StationWithLeakData = WaterStation & { isLeaking: boolean } & { datasources: DataSource[] }

const getNodePosition = (station: WaterStation, i: number) => {
  if (station.ui.position) {
    return station.ui.position
  }
  return {
    x: station.station_type === 'entry' ? 0 : station.station_type === 'usage' ? X_OFFSET : X_OFFSET * 2,
    y: i * Y_OFFSET
  }
}

const makeNodes = (stations: StationWithLeakData[]) => {
  const [entries, usages, discharges] = stations.reduce(
    (acc, station) => {
      if (station.station_type === 'entry') {
        acc[0].push(station)
      } else if (station.station_type === 'usage') {
        acc[1].push(station)
      } else {
        acc[2].push(station)
      }
      return acc
    },
    [[], [], []] as StationWithLeakData[][]
  )
  const entryNodes = entries.map<CanvasNodeTypes['source']>((station, i) => ({
    type: 'source',
    id: station.id,
    position: getNodePosition(station, i),
    data: {
      station: station
    }
  }))
  const usageNodes = usages.map<CanvasNodeTypes['station']>((station, i) => ({
    type: 'station',
    id: station.id,
    position: getNodePosition(station, i),
    data: {
      station: station
    }
  }))
  const dischargeNodes = discharges.map<CanvasNodeTypes['discharge']>((station, i) => ({
    type: 'discharge',
    id: station.id,
    position: getNodePosition(station, i),
    data: {
      station: station
    }
  }))
  return [...entryNodes, ...usageNodes, ...dischargeNodes]
}

const makeEdges = (connections: Connection[], readonly: boolean): Edge[] => {
  return connections.map(({ id, source_station_id: source, target_station_id: target }) => ({
    id: `${id}`,
    source,
    target,
    style: {
      strokeWidth: 2
    },
    selectable: !readonly
  }))
}

type MapLayoutCanvasProps = {
  stations: StationWithLeakData[]
  connections: Connection[]
  handleConnect: ({ source, target }: { source: WaterStation; target: WaterStation }) => void
  handleDisconnect: (connectionId: string) => void
  handlePositionChange: (stationId: string, position: { x: number; y: number }) => void
  readonly?: boolean
}

const MapLayoutCanvas = ({
  stations,
  connections,
  handleDisconnect,
  handleConnect,
  handlePositionChange,
  readonly
}: MapLayoutCanvasProps) => {
  const [locked, setLocked] = useState(readonly ?? true)
  const [nodes, setNodes, onNodesChange] = useNodesState(makeNodes(stations))
  const [edges, setEdges, onEdgesChange] = useEdgesState(makeEdges(connections, locked))
  const [theme] = useThemeAppearance()

  useEffect(() => {
    setNodes(makeNodes(stations))
    setEdges(makeEdges(connections, locked))
  }, [stations, connections, locked])

  const onNodesConnect: OnConnect = ({ source, target }) => {
    const sourceStation = stations.find(
      (s) => s.id === (nodes.find((n) => n.id === source)?.data.station.id ?? 'sourceId')
    )
    const targetStation = stations.find(
      (s) => s.id === (nodes.find((n) => n.id === target)?.data.station.id ?? 'targetId')
    )
    if (!sourceStation || !targetStation) {
      return
    }
    // this is proactive update to avoid ui glitch
    setEdges((edges) => [...edges, { id: `${_.uniqueId()}`, source, target }])
    // this is the actual update
    // todo rollback if the mutation fails
    handleConnect({ source: sourceStation, target: targetStation })
  }

  return (
    <ReactFlow
      colorMode={theme !== 'inherit' ? theme : 'system'}
      nodeTypes={CanvasNodes}
      nodes={nodes}
      onNodesChange={onNodesChange}
      edges={edges}
      fitView
      fitViewOptions={{
        padding: 0.1,
        maxZoom: 1
      }}
      deleteKeyCode={['Delete', 'Backspace']}
      onNodeDragStop={(_, node) => handlePositionChange(node.data.station.id, node.position)}
      draggable={false}
      onConnect={onNodesConnect}
      onEdgesDelete={(edgesToDelete) => {
        handleDisconnect(edgesToDelete[0].id)
      }}
      onEdgesChange={onEdgesChange}
      nodesDraggable={!locked}
      nodesConnectable={!locked}
      edgesFocusable={!locked}
      panOnDrag={!locked}
      zoomOnScroll={!locked}
      zoomOnPinch={!locked}
      minZoom={0.1}
    >
      <Controls showInteractive={false} style={{ zoom: 1 }}>
        {!readonly && (
          <ControlButton onClick={() => setLocked((prev) => !prev)}>
            <Tooltip content={'Use this to unlock the canvas'}>
              <div>
                {locked ? (
                  <LuLock size={18} className="!fill-none" />
                ) : (
                  <LuLockOpen size={18} className="!fill-none" />
                )}
              </div>
            </Tooltip>
          </ControlButton>
        )}
      </Controls>
      {!locked && !readonly && <MiniMap className="absolute mb-[-10px] mr-[-10px] z-50" />}
    </ReactFlow>
  )
}

export { MapLayoutCanvas }
